import { SRGBColorSpace } from 'three';

import { renderOrders } from '../../render-orders';
import { OrthoTileAsset } from '../ortho-tile-asset/ortho-tile-asset';
import { TileCache } from '../ortho-tile-asset/tile-cache';
import { getCogProjection } from './cog-projection';
import { CogSource } from './cog-source';
import { CogTileLoader } from './cog-tile-loader';
import { CogMaterialOptions, CogWorldMapping } from './cog-world-mapping';

/** 512 px tiles take ~1.25 MB of GPU memory each, mask included. */
const MAX_TEXTURES = 160;
/**
 * Deliberately above the browser's connection limit: the range reader merges the tiles
 * requested in a frame into a few requests, so more tiles in flight means fewer requests.
 */
const MAX_CONCURRENT_TILES = 32;
/** Same magnification range as the XYZ asset (384 px split for 256 px tiles). */
const SPLIT_THRESHOLD_PX = 768;

const DEFAULT_MATERIAL: CogMaterialOptions = {
  colorSpace: SRGBColorSpace,
  polygonOffset: { factor: -1, units: -1 },
  transparent: true,
  depthWrite: false,
  renderOrder: renderOrders.orthoPhoto,
};

/**
 * Builds an OrthoTileAsset that streams an opened COG. Tiles are placed in the project CRS
 * relative to sceneOriginProject.
 */
export function createCogOrthoAsset(params: {
  id: string;
  source: CogSource;
  projectProjection: string;
  /** Project coords at scene (0,0). */
  sceneOriginProject: { x: number; y: number };
  material?: Partial<CogMaterialOptions>;
}): OrthoTileAsset {
  const { id, source, projectProjection, sceneOriginProject, material } = params;

  return new OrthoTileAsset({
    id,
    loader: new CogTileLoader(source),
    cache: new TileCache({ maxTextures: MAX_TEXTURES, maxConcurrent: MAX_CONCURRENT_TILES }),
    keepRootsVisible: true,
    splitThresholdPx: SPLIT_THRESHOLD_PX,
    levels: source.getLevels(),
    worldMapping: new CogWorldMapping({
      source,
      cogToProject: getCogProjection(source, projectProjection).toProject,
      sceneOriginProject,
      material: { ...DEFAULT_MATERIAL, ...material },
    }),
  });
}
